import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { extractSfdcId } from "@amzn/afxtaco-utilities";
import { validateCustomerContext } from "@/utils/customerContext";

export interface ContextFields {
  accountId: string;
  opportunityId: string;
}

interface OptionalContextFieldsProps {
  fields: ContextFields;
  onChange: (fields: ContextFields) => void;
  /**
   * Whether to surface format errors. Off by default so this stays exactly as it
   * was before the Overview feature; the homepage passes the flag.
   */
  showValidation?: boolean;
}

/**
 * Collapsible "Give TACO additional context" section with optional fields for
 * Opportunity ID and Account ID. Lives inside the homepage form card, below
 * the textarea and above the toolbar.
 *
 * These IDs are persisted on the project and handed to the agent as confirmed
 * customer context — it grounds on them without asking the user to confirm. So
 * format is validated here, using the same rules as the Overview tab's Customer
 * box and the API, to catch a mistyped or half-pasted ID before it costs a turn.
 *
 * Errors show only once a field is touched and non-empty: both fields are
 * optional, and flagging an empty one would be noise.
 *
 * Validation is opt-in via `showValidation`, which the homepage ties to the
 * `overviewPage` flag: these fields predate that feature and, with it off, an ID
 * here is only prompt text the agent may interpret loosely. Nothing downstream
 * treats it as confirmed, so there is no mistake to warn about yet.
 */
const OptionalContextFields: React.FC<OptionalContextFieldsProps> = ({
  fields,
  onChange,
  showValidation = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [touched, setTouched] = useState<Record<keyof ContextFields, boolean>>({
    accountId: false,
    opportunityId: false,
  });

  const errors = validateCustomerContext(fields);

  const handleChange = (field: keyof ContextFields, value: string) => {
    // Accepts a pasted Salesforce Lightning URL as well as a bare ID — the
    // field always ends up holding the clean ID, so validation, the prompt
    // text, and persistence downstream don't need to know URLs were ever
    // involved.
    onChange({ ...fields, [field]: extractSfdcId(value) });
  };

  const markTouched = (field: keyof ContextFields) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const errorFor = (field: keyof ContextFields) => {
    if (!showValidation) return undefined;
    return touched[field] && fields[field].trim() ? errors[field] : undefined;
  };

  return (
    <div className="border-t border-border/50 mx-5">
      {/* Toggle header */}
      <button
        type="button"
        className="flex items-center gap-1.5 py-3 cursor-pointer select-none w-full"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <ChevronDown
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
        <span className="text-xs font-medium text-muted-foreground">
          Give TACO additional context
        </span>
        <span className="text-[10px] bg-muted text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-full font-medium">
          Optional
        </span>
      </button>

      {/* Fields grid */}
      <div
        className={`overflow-hidden transition-all duration-250 ease-in-out ${
          isExpanded ? "max-h-[200px] opacity-100 pb-3" : "max-h-0 opacity-0"
        }`}
      >
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="context-opportunity-id"
              className="text-[11px] font-medium text-muted-foreground"
            >
              Salesforce Opportunity ID / URL
            </label>
            <input
              id="context-opportunity-id"
              type="text"
              value={fields.opportunityId}
              onChange={(e) => handleChange("opportunityId", e.target.value)}
              onBlur={() => markTouched("opportunityId")}
              aria-invalid={Boolean(errorFor("opportunityId"))}
              placeholder="e.g. 006ABC00001234WXYZ"
              className={`px-2.5 py-2 border rounded-lg text-sm bg-background text-foreground placeholder:text-muted-foreground focus:ring-1 outline-none transition-all duration-150 ${
                errorFor("opportunityId")
                  ? "border-red-500 focus:ring-red-500/20"
                  : "border-border focus:border-blue-500 focus:ring-blue-500/20"
              }`}
            />
            {errorFor("opportunityId") && (
              <p className="text-[11px] text-red-600 dark:text-red-400">
                {errorFor("opportunityId")}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="context-account-id"
              className="text-[11px] font-medium text-muted-foreground"
            >
              Salesforce Account ID / URL
            </label>
            <input
              id="context-account-id"
              type="text"
              value={fields.accountId}
              onChange={(e) => handleChange("accountId", e.target.value)}
              onBlur={() => markTouched("accountId")}
              aria-invalid={Boolean(errorFor("accountId"))}
              placeholder="e.g. 00100000001234WXYZ"
              className={`px-2.5 py-2 border rounded-lg text-sm bg-background text-foreground placeholder:text-muted-foreground focus:ring-1 outline-none transition-all duration-150 ${
                errorFor("accountId")
                  ? "border-red-500 focus:ring-red-500/20"
                  : "border-border focus:border-blue-500 focus:ring-blue-500/20"
              }`}
            />
            {errorFor("accountId") && (
              <p className="text-[11px] text-red-600 dark:text-red-400">
                {errorFor("accountId")}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OptionalContextFields;
